import { useMemo, useState } from 'react';
import { Columns2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { useCreateNudge, useNudgeFields, useUpdateNudge } from '@/hooks/use-sales-nudges';
import {
	buildNudgePayload,
	newNudgeDraft,
	nudgeToDraft,
	NUDGE_NAME_MAX_LENGTH,
	type NudgeDraft,
	type NudgePayloadErrorSection
} from '../nudge-draft';
import { toFieldMap } from '../nudge-rules';
import type { Nudge, NudgeField } from '../types';
import { PILL_BUTTON } from '../ui-chrome';
import { NudgeRulesEditor } from './nudge-rules-editor';
import { NudgeMessageEditor } from './nudge-message-editor';

export type NudgePanelMode = 'view' | 'edit';

/** What the panel shows: a saved nudge in either mode, or a new one (always edited) and its "Nudge N" name. */
export type NudgePanelState =
	| { mode: NudgePanelMode; nudge: Nudge }
	| { mode: 'edit'; nudge: null; defaultName: string };

interface NudgePanelProps {
	state: NudgePanelState | null;
	onStateChange: (state: NudgePanelState | null) => void;
}

interface NudgePanelBodyProps {
	state: NudgePanelState;
	fields: NudgeField[];
	expanded: boolean;
	onToggleExpanded: () => void;
	onStateChange: (state: NudgePanelState | null) => void;
}

/**
 * Keyed by the caller on nudge and mode, so the working copy starts fresh each time the panel
 * switches between viewing and editing: Cancel needs no reset logic, it just switches back.
 */
const NudgePanelBody = ({ state, fields, expanded, onToggleExpanded, onStateChange }: NudgePanelBodyProps) => {
	const { mode, nudge } = state;
	const editable = mode === 'edit';
	const [draft, setDraft] = useState<NudgeDraft>(() =>
		state.nudge ? nudgeToDraft(state.nudge) : newNudgeDraft(state.defaultName)
	);
	// Errors show only after a save attempt, then track the draft live as it is fixed.
	const [attempted, setAttempted] = useState(false);
	const createMutation = useCreateNudge();
	const updateMutation = useUpdateNudge();
	const isSaving = createMutation.isPending || updateMutation.isPending;

	const fieldMap = useMemo(() => toFieldMap(fields), [fields]);
	const payload = buildNudgePayload(draft, fieldMap);
	const errorIn = (section: NudgePayloadErrorSection) =>
		attempted && !payload.ok && payload.section === section ? payload.error : null;

	const update = (patch: Partial<NudgeDraft>) => setDraft((current) => ({ ...current, ...patch }));

	const onSaved = (saved: Nudge) => {
		toast.success(`Saved ${saved.name}`);
		onStateChange({ mode: 'view', nudge: saved });
	};
	const onError = (err: unknown) => toast.error(getApiErrorMessage(err, 'Could not save the nudge'));

	const save = () => {
		setAttempted(true);
		if (!payload.ok || isSaving) return;
		if (nudge) {
			updateMutation.mutate({ nudgeId: nudge.id, payload: payload.value }, { onSuccess: onSaved, onError });
		} else {
			createMutation.mutate(payload.value, { onSuccess: onSaved, onError });
		}
	};

	// Cancel on a new nudge has nothing to return to, so it closes the panel.
	const cancel = () => onStateChange(nudge ? { mode: 'view', nudge } : null);

	return (
		<>
			<div className='flex h-[60px] shrink-0 items-center justify-between gap-3 border-b border-gray-300 bg-gray-50 px-4'>
				{editable ? (
					<>
						<SheetTitle className='sr-only'>{draft.name || 'New nudge'}</SheetTitle>
						<input
							value={draft.name}
							maxLength={NUDGE_NAME_MAX_LENGTH}
							onChange={(event) => update({ name: event.target.value })}
							aria-label='Nudge name'
							className='field-sizing-content min-w-16 max-w-full border-0 bg-transparent p-0 text-base leading-[1.1] font-semibold text-ink underline decoration-ink decoration-dotted decoration-[12%] [text-decoration-skip-ink:none] [text-underline-position:from-font] outline-none'
						/>
					</>
				) : (
					<SheetTitle className='truncate text-sm leading-5 font-semibold text-ink'>{nudge?.name}</SheetTitle>
				)}
				<div className='flex shrink-0 items-center gap-1.5'>
					{editable ? (
						<>
							<Button
								type='button'
								variant='outline'
								disabled={isSaving}
								onClick={cancel}
								className={cn(PILL_BUTTON, 'px-3 shadow-none')}
							>
								Cancel
							</Button>
							<Button type='button' disabled={isSaving} onClick={save} className={cn(PILL_BUTTON, 'px-3')}>
								{isSaving && <Loader2 className='size-3.5 animate-spin' />}
								Save
							</Button>
						</>
					) : (
						<Button
							type='button'
							variant='outline'
							onClick={() => onStateChange({ ...state, mode: 'edit' })}
							className={cn(PILL_BUTTON, 'px-3 shadow-none')}
						>
							Edit
						</Button>
					)}
					<Button
						type='button'
						variant='ghost'
						size='icon'
						onClick={onToggleExpanded}
						aria-label={expanded ? 'Collapse panel' : 'Expand panel'}
						aria-pressed={expanded}
						className='ml-1.5 text-ink'
					>
						<Columns2 className='size-4' />
					</Button>
				</div>
			</div>

			<div className='flex flex-1 flex-col gap-4 overflow-y-auto p-3'>
				<NudgeRulesEditor
					rows={draft.rules}
					fields={fields}
					editable={editable}
					error={errorIn('rules')}
					onChange={(rules) => update({ rules })}
				/>
				<NudgeMessageEditor
					prompt={draft.prompt}
					suggestions={draft.suggestions}
					editable={editable}
					error={errorIn('other')}
					onPromptChange={(prompt) => update({ prompt })}
					onSuggestionsChange={(suggestions) => update({ suggestions })}
				/>
			</div>
		</>
	);
};

const NudgePanelSkeleton = () => (
	<div className='flex flex-col gap-4 p-3'>
		<SheetTitle className='sr-only'>Loading nudge</SheetTitle>
		<Skeleton className='h-[60px] w-full' />
		<Skeleton className='h-56 w-full rounded-2xl' />
		<Skeleton className='h-72 w-full rounded-2xl' />
	</div>
);

/** Closing the panel (overlay, Escape) discards an unsaved edit, as Cancel does. */
export const NudgePanel = ({ state, onStateChange }: NudgePanelProps) => {
	const [expanded, setExpanded] = useState(false);
	const { data: fields, isLoading, error } = useNudgeFields();
	const bodyKey = state ? `${state.nudge?.id ?? 'new'}:${state.mode}:${state.nudge?.updatedAt ?? ''}` : 'closed';

	return (
		<Sheet open={state !== null} onOpenChange={(open) => !open && onStateChange(null)}>
			<SheetContent
				side='right'
				showClose={false}
				aria-describedby={undefined}
				className={cn(
					'w-full gap-0 border-gray-300 bg-gray-100 p-0 backdrop-blur-none sm:max-w-none',
					expanded ? 'sm:w-[calc(100vw-48px)]' : 'sm:w-[680px]'
				)}
			>
				{isLoading && <NudgePanelSkeleton />}
				{error && (
					<>
						<SheetTitle className='sr-only'>Nudge</SheetTitle>
						<p className='px-4 py-8 text-center text-sm text-destructive'>
							{getApiErrorMessage(error, 'Could not load the nudge fields. Please try again.')}
						</p>
					</>
				)}
				{state && fields && (
					<NudgePanelBody
						key={bodyKey}
						state={state}
						fields={fields}
						expanded={expanded}
						onToggleExpanded={() => setExpanded((current) => !current)}
						onStateChange={onStateChange}
					/>
				)}
			</SheetContent>
		</Sheet>
	);
};
